import { Component, EventEmitter, Inject, Input, OnInit, Optional, Output } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PaymentCardModel, PaymentCardTypes } from './paymentcard.model';
import { BankAccountModel } from '../bancos/bankaccount.model';
import { BankAccountService } from '../bancos/bankaccount.service';
import { PaymentCardService } from './paymentcard.service';
import { Utils } from '../../../shared/utils';
import { AccountMovementsViewModel } from '../../accounting/models/accounting-movement.model';
import { SharedService } from '../../../shared/services/shared.service';
import { PaymentCardDialogImportAccount } from './paymentcard-dialog-importAccount.component';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';

export interface PaymentCardFormDialogData {
  uid?: string | null;
  paymentCardType?: number;
  movement?: AccountMovementsViewModel;
  type?: string;
  flagImport?: boolean;
}

@Component({
    selector: 'convfact-paymentcard-form',
    templateUrl: './paymentcard-form.component.html',
    styleUrls: ['./paymentcard-form.component.css'],
    standalone: false
})
export class PaymentCardFormComponent implements OnInit {
  @Input() paymentCardUid: string;
  @Input() paymentCardType: number;
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  public paymentCard: PaymentCardModel = new PaymentCardModel();
  public bankAccounts: BankAccountModel[] = [];
  public expiring = '';
  public ready = false;
  public loadError = false;
  public saving = false;
  public submitted = false;
  public saveError = '';

  flagImport = false;
  movement: AccountMovementsViewModel;
  type: string;

  static open(dialog: MatDialog, data: PaymentCardFormDialogData): Observable<boolean> {
    return dialog.open<PaymentCardFormComponent, PaymentCardFormDialogData, boolean>(PaymentCardFormComponent, {
      width: '580px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private bankAccountService: BankAccountService,
    private paymentCardService: PaymentCardService,
    private sharedService: SharedService,
    private notify: NotificationService,
    public dialog: MatDialog,
    @Optional() public dialogRef: MatDialogRef<any, boolean>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: PaymentCardFormDialogData,
  ) { }

  get isEdit(): boolean {
    return this.isGuid(this.paymentCardUid);
  }

  get dialogTitle(): string {
    const kind = this.paymentCardType === PaymentCardTypes.Tarjeta_Credito
      ? 'tarjeta de crédito'
      : this.paymentCardType === PaymentCardTypes.Tarjeta_Debito ? 'tarjeta de débito' : 'tarjeta';
    return `${this.isEdit ? 'Editar' : 'Nueva'} ${kind}`;
  }

  get missingBankAccount(): boolean {
    return Utils.IsNullOrEmpty(this.paymentCard.bankAccountUid);
  }

  get missingName(): boolean {
    return !(this.paymentCard.name || '').trim();
  }

  get missingNumber(): boolean {
    return !(this.paymentCard.number || '').trim();
  }

  get invalidExpiring(): boolean {
    const value = (this.expiring || '').trim();
    if (!value) return false;
    if (!value.includes('/')) return true;
    const parts = value.split('/');
    const month = parseInt(parts[0], 10);
    const year = parseInt(parts[1], 10);
    return isNaN(month) || month < 1 || month > 12 || isNaN(year) || year < 0;
  }

  get hasErrors(): boolean {
    return this.missingBankAccount || this.missingName || this.missingNumber || this.invalidExpiring;
  }

  public ngOnInit(): void {
    if (this.data) {
      if (this.paymentCardType == null) this.paymentCardType = this.data.paymentCardType;
      if (!this.paymentCardUid && this.isGuid(this.data.uid)) this.paymentCardUid = this.data.uid;
    }

    if (this.data && this.data.movement != undefined) {
      this.movement = this.data.movement;
      this.type = this.data.type;
      this.movement.type = this.type;
      this.flagImport = true;
      this.paymentCard.manualMovement = this.movement;
      this.paymentCard.type = 1;
      this.sharedService.checkIfAccountNumberExists(this.movement.accountNumber, this.movement.type).then(response => {
        this.paymentCard.accountNumber = response;
        this.paymentCard.manualMovement.accountNumber = response;
      });
    }

    this.load();
  }

  public load(): void {
    this.ready = false;
    this.loadError = false;
    Promise.all([
      this.bankAccountService.getLookupList(),
      this.isEdit ? this.paymentCardService.loadData(this.paymentCardUid) : Promise.resolve(null),
    ])
      .then(([accounts, card]) => {
        this.bankAccounts = accounts || [];
        if (this.isEdit) {
          if (!card) throw new Error();
          this.paymentCard = card;
          this.expiring = card.monthExpiring && card.yearExpiring ? card.monthExpiring + '/' + card.yearExpiring : '';
        }
        this.applySingleOptionDefaults();
        this.ready = true;
      })
      .catch(() => {
        this.loadError = true;
        this.ready = true;
      });
  }

  public save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';
    if (this.hasErrors) return;

    const paymentCard: PaymentCardModel = { ...this.paymentCard };
    paymentCard.type = this.paymentCardType;

    const expiring = (this.expiring || '').trim();
    if (expiring) {
      const expiry = expiring.split('/');
      paymentCard.monthExpiring = parseInt(expiry[0]);
      paymentCard.yearExpiring = parseInt(expiry[1]);
    } else {
      paymentCard.monthExpiring = 0;
      paymentCard.yearExpiring = 0;
    }

    paymentCard.number = paymentCard.number.replace(/\d(?=\d{4})/g, '*');

    this.setSaving(true);

    if (paymentCard.uid == null) {
      this.paymentCardService.addData(paymentCard)
        .then(() => this.finish('Tarjeta creada correctamente'))
        .catch(error => {
          const message = this.errorMessage(error);
          if (message.includes('ya se encuentra registrada') && this.data && this.data.type != null) {
            this.setSaving(false);
            this.dialog.open(PaymentCardDialogImportAccount, {
              width: '540px',
              maxWidth: '96vw',
              autoFocus: false,
              data: { dataImport: paymentCard },
            }).afterClosed().subscribe(imported => this.close(imported === true));
            return;
          }
          this.fail(message || 'No se pudo crear la tarjeta');
        });
      return;
    }

    this.paymentCardService.editData(paymentCard.uid, paymentCard)
      .then(() => this.finish('Tarjeta actualizada correctamente'))
      .catch(error => this.fail(this.errorMessage(error) || 'No se pudo actualizar la tarjeta'));
  }

  public cancel(): void {
    if (this.saving) return;
    this.close(false);
  }

  private finish(message: string): void {
    this.notify.ok(message);
    this.setSaving(false);
    this.close(true);
  }

  private fail(message: string): void {
    this.setSaving(false);
    this.saveError = message;
    this.notify.error(message);
  }

  private close(changed: boolean): void {
    if (this.dialogRef) {
      this.dialogRef.close(changed);
      return;
    }
    if (changed) this.saved.emit();
    else this.cancelled.emit();
  }

  private setSaving(saving: boolean): void {
    this.saving = saving;
    if (this.dialogRef) this.dialogRef.disableClose = saving;
  }

  private applySingleOptionDefaults(): void {
    if (this.missingBankAccount && this.bankAccounts.length === 1) {
      this.paymentCard.bankAccountUid = this.bankAccounts[0].uid;
    }
  }

  private errorMessage(error: any): string {
    const body = error && error.error;
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        return (parsed && parsed.message) || '';
      } catch {
        return '';
      }
    }
    return (body && typeof body.message === 'string') ? body.message : '';
  }

  private isGuid(value: string | null | undefined): boolean {
    return !!value && value.length === 36;
  }
}
